import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SearchableCombobox } from "../../components/SearchableCombobox";
import {
  deleteSetting,
  fetchSettingsCatalogue,
  listSettings,
  listWatiTemplates,
  refreshWatiTemplates,
  testSetting,
  upsertSetting,
} from "../../lib/adminApi";

function StatusPill({ enabled, hasSecrets }) {
  if (enabled && hasSecrets) {
    return (
      <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold" data-testid="status-live">
        Live
      </span>
    );
  }
  if (hasSecrets) {
    return (
      <span className="rounded-full bg-elevated px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted" data-testid="status-disabled">
        Disabled
      </span>
    );
  }
  return (
    <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted" data-testid="status-empty">
      Not configured
    </span>
  );
}

function FieldControl({ field, value, onChange, testIdPrefix, extra }) {
  const testId = `${testIdPrefix}-${field.name}`;
  if (field.kind === "select") {
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
      >
        <option value="">Select…</option>
        {(field.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  if (field.kind === "template_select") {
    const templates = extra?.templates || [];
    return (
      <SearchableCombobox
        value={value || ""}
        onChange={onChange}
        options={templates}
        placeholder={field.placeholder}
        emptyLabel="No templates cached yet — click Download templates below."
        testId={testId}
      />
    );
  }
  return (
    <Input
      type={field.kind === "email" ? "email" : field.kind === "url" ? "url" : "text"}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder || ""}
      data-testid={testId}
    />
  );
}

function ProviderCard({ spec, initial, onSaved }) {
  // Always enabled — the enable/disable toggle was removed per product spec (07-Feb-2026).
  // Providers activate the moment credentials are saved.
  const enabled = true;
  const [config, setConfig] = useState({ ...(initial?.config || {}) });
  // Secret inputs are write-only: empty string = don't change.
  const [secrets, setSecrets] = useState(() => {
    const s = {};
    for (const f of spec.secret_fields) s[f.name] = "";
    return s;
  });
  const [clearSecrets, setClearSecrets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTarget, setTestTarget] = useState("");
  const [lastResult, setLastResult] = useState(null);

  // WATI-specific: cached template library (loaded once + refreshable via the button below).
  const isWati = spec.provider === "wati";
  const [templates, setTemplates] = useState([]);
  const [templatesCachedAt, setTemplatesCachedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isWati) return;
    listWatiTemplates()
      .then((res) => {
        setTemplates(res.templates || []);
        setTemplatesCachedAt(res.cached_at || null);
      })
      .catch(() => {
        // silently ignore — first-time users will just see an empty list until they Download
      });
  }, [isWati]);

  const savedPreview = initial?.secrets_preview || {};
  const savedSet = new Set(initial?.secrets_set || []);
  const hasSecrets = savedSet.size > 0;
  const dirty = useMemo(() => {
    if (JSON.stringify(config) !== JSON.stringify(initial?.config || {})) return true;
    if (Object.values(secrets).some((v) => v && v.length > 0)) return true;
    if (clearSecrets.length > 0) return true;
    return false;
  }, [config, secrets, clearSecrets, initial]);

  function updateField(name, value) {
    setConfig((c) => ({ ...c, [name]: value }));
  }
  function updateSecret(name, value) {
    setSecrets((s) => ({ ...s, [name]: value }));
    setClearSecrets((cs) => cs.filter((n) => n !== name));
  }
  function toggleClear(name) {
    setClearSecrets((cs) => (cs.includes(name) ? cs.filter((n) => n !== name) : [...cs, name]));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        enabled,
        config,
        new_secrets: Object.fromEntries(Object.entries(secrets).filter(([, v]) => v && v.length > 0)),
        clear_secrets: clearSecrets,
      };
      const updated = await upsertSetting(spec.provider, payload);
      toast.success(`${spec.label} saved.`);
      // Reset write-only fields after save
      const cleared = {};
      for (const f of spec.secret_fields) cleared[f.name] = "";
      setSecrets(cleared);
      setClearSecrets([]);
      onSaved(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    // For WATI the test is a connectivity check (target is ignored). For ZeptoMail we still
    // need a recipient email.
    if (spec.provider !== "wati" && !testTarget.trim()) {
      toast.error(spec.test_input_label);
      return;
    }
    setTesting(true);
    setLastResult(null);
    try {
      const res = await testSetting(spec.provider, testTarget.trim());
      setLastResult(res);
      if (res.ok) toast.success(res.detail);
      else toast.error(res.detail);
    } catch (err) {
      const detail = err.response?.data?.detail || "Test failed.";
      setLastResult({ ok: false, detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  }

  async function handleDownloadTemplates() {
    setRefreshing(true);
    try {
      const res = await refreshWatiTemplates();
      setTemplates(res.templates || []);
      setTemplatesCachedAt(res.cached_at);
      toast.success(`Downloaded ${res.count} approved template${res.count === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download templates.");
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid={`provider-card-${spec.provider}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">
            {spec.channel === "email" ? "Email" : spec.channel === "whatsapp" ? "WhatsApp" : "AI"}
          </p>
          <h3 className="mt-1 text-lg font-semibold text-text">{spec.label}</h3>
          <p className="mt-2 max-w-2xl text-xs text-muted">{spec.description}</p>
        </div>
        <StatusPill enabled={hasSecrets} hasSecrets={hasSecrets} />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {spec.config_fields.map((f) => (
          <div
            key={f.name}
            className={`flex flex-col gap-1.5 ${f.kind === "template_select" ? "sm:col-span-2" : ""}`}
          >
            <Label htmlFor={`${spec.provider}-${f.name}`}>
              {f.label} {f.required && <span className="text-gold">*</span>}
            </Label>
            <FieldControl
              field={f}
              value={config[f.name] || ""}
              onChange={(v) => updateField(f.name, v)}
              testIdPrefix={`config-${spec.provider}`}
              extra={{ templates }}
            />
            {f.help && <p className="text-xs text-muted">{f.help}</p>}
            {f.kind === "template_select" && (
              <div className="mt-2 flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplates}
                  disabled={refreshing || !hasSecrets}
                  data-testid={`download-templates-${spec.provider}`}
                >
                  {refreshing ? "Downloading…" : templates.length > 0 ? "Refresh templates" : "Download templates"}
                </Button>
                {templates.length > 0 && (
                  <span className="text-xs text-muted" data-testid={`templates-cached-${spec.provider}`}>
                    {templates.length} approved template{templates.length === 1 ? "" : "s"}
                    {templatesCachedAt && ` · downloaded ${new Date(templatesCachedAt).toLocaleString()}`}
                  </span>
                )}
                {!hasSecrets && (
                  <span className="text-xs text-muted">Save the endpoint + token first.</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h4 className="text-xs uppercase tracking-widest text-muted">Secrets</h4>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {spec.secret_fields.map((f) => {
            const isSet = savedSet.has(f.name);
            const beingCleared = clearSecrets.includes(f.name);
            return (
              <div key={f.name} className="flex flex-col gap-1.5">
                <Label htmlFor={`${spec.provider}-${f.name}`}>
                  {f.label} {f.required && !isSet && <span className="text-gold">*</span>}
                </Label>
                {isSet && (
                  <div
                    className="flex items-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-400/5 px-3 py-2 text-xs"
                    data-testid={`saved-secret-${spec.provider}-${f.name}`}
                  >
                    <span className="text-emerald-300">Currently stored</span>
                    <code className="font-mono text-text">{savedPreview[f.name]}</code>
                    <span className="ml-auto text-[10px] uppercase tracking-widest text-muted">
                      encrypted at rest
                    </span>
                  </div>
                )}
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={secrets[f.name] || ""}
                  onChange={(e) => updateSecret(f.name, e.target.value)}
                  placeholder={isSet ? "Leave blank to keep the stored value — type here to replace" : (f.placeholder || "")}
                  data-testid={`secret-${spec.provider}-${f.name}`}
                />
                {isSet && (
                  <label className="mt-1 flex items-center gap-2 text-xs text-muted">
                    <input
                      type="checkbox"
                      checked={beingCleared}
                      onChange={() => toggleClear(f.name)}
                      className="accent-gold"
                      data-testid={`clear-secret-${spec.provider}-${f.name}`}
                    />
                    Clear this secret on save
                  </label>
                )}
                {f.help && <p className="text-xs text-muted">{f.help}</p>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid={`save-${spec.provider}`}
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
        {initial?.updated_at && (
          <span className="text-xs text-muted" data-testid={`updated-${spec.provider}`}>
            Updated {new Date(initial.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="mt-6 rounded-md border border-border/80 bg-elevated/50 p-4">
        <div className="text-xs uppercase tracking-widest text-muted">Test connection</div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
          {isWati ? (
            <p className="flex-1 text-xs text-muted">
              WATI test hits <span className="font-mono text-gold">getMessageTemplates</span> to verify
              your endpoint and token — no message credits used, no template selection needed yet.
            </p>
          ) : (
            <Input
              value={testTarget}
              onChange={(e) => setTestTarget(e.target.value)}
              placeholder={spec.test_input_label}
              data-testid={`test-target-${spec.provider}`}
            />
          )}
          <Button variant="outline" onClick={handleTest} disabled={testing || !hasSecrets} data-testid={`test-${spec.provider}`}>
            {testing ? "Testing…" : isWati ? "Test connection" : "Send test"}
          </Button>
        </div>
        {!hasSecrets && <p className="mt-2 text-xs text-muted">Save credentials first, then run a test.</p>}
        {lastResult && (
          <p
            className={`mt-3 text-xs ${lastResult.ok ? "text-gold" : "text-red-400"}`}
            data-testid={`test-result-${spec.provider}`}
          >
            {lastResult.ok ? "✓ " : "✗ "}{lastResult.detail}
          </p>
        )}
      </div>
    </div>
  );
}

export function AdminSettingsPage() {
  const [specs, setSpecs] = useState([]);
  const [settingsByProvider, setSettingsByProvider] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogue, saved] = await Promise.all([fetchSettingsCatalogue(), listSettings()]);
      setSpecs(catalogue);
      const map = {};
      for (const s of saved) map[s.provider] = s;
      setSettingsByProvider(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load master settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-8" data-testid="admin-settings-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Integrations</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Integrations</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Provider credentials are encrypted at rest and only their first & last four characters are
          ever shown. Toggle a provider on to switch OTP delivery from dev-mode logging to live sending.
        </p>
      </div>

      {loading ? (
        <div className="rounded-lg border border-border bg-card p-10 text-center text-muted">Loading…</div>
      ) : (
        <div className="grid gap-6">
          {specs.map((spec) => (
            <ProviderCard
              key={spec.provider}
              spec={spec}
              initial={settingsByProvider[spec.provider]}
              onSaved={(updated) =>
                setSettingsByProvider((prev) => ({ ...prev, [spec.provider]: updated }))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
